/**
 * UX-02 slice 3: the submit→stream orchestration extracted verbatim from
 * App.tsx's handleSendMessage real-API branch.
 *
 * The hook owns the stream lifecycle — submitCommand, the SSE handler web
 * (thinking/final channels, widgets, error/done), the idle + max-duration
 * watchdogs, and stop/cancel — while App.tsx keeps what it is genuinely
 * responsible for: thread/message assembly, the patient-context bridge, and
 * the exports-drawer side effects (injected as deps). Pure decode/reduce logic
 * stayed in slices 1–2 (decodeResponseMetadata, streamStateReducer); this
 * slice moves the effectful shell.
 *
 * Refresh-resume (2026-08-11): the stream-attachment half is factored out of
 * submit (attachStreamForAssistant) so a reloaded page can re-attach to a run
 * the backend kept alive. While streaming, a sessionStorage marker records
 * which conversation/thread/bubble the stream feeds; on mount the hook probes
 * GET /api/v1/chat/active and either re-attaches with a full replay
 * (Last-Event-ID: 0) or recovers the persisted turn from
 * /v1/conversations/{id}/turns.
 *
 * Verified live per the local e2e recipe: seeded patient → /summary → thinking
 * events stream into the reasoning panel → final answer + metadata render.
 */
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';

import {
  buildChatStreamUrl,
  cancelCommandRun,
  fetchChatStreamStatus,
  openCommandStream,
  submitCommand,
} from '../api/commandApi';
import { fetchConversationTurns } from '../api/conversationsApi';
import {
  clearActiveStream as clearActiveStreamRecord,
  readActiveStream,
  recordActiveStream,
} from '../services/streamResumeStore';
import type { MessageWithDate } from '../services/threadService';
import type { NormalizedThinkingEvent } from '../components/thinking/types';
import { normalizeThinkingEvent } from '../components/thinking/normalizeThinkingEvent';
import { decodeResponseMetadata } from '../components/thinking/decodeResponseMetadata';
import { chartBriefingNarrativeFromWidget } from '../components/response/contentUtils';
import {
  INITIAL as STREAM_INITIAL,
  markAllThinkingStepsComplete,
  streamStateReducer,
} from '../components/thinking/streamStateReducer';

export type StreamWidget = { type: string; data: Record<string, unknown> };

/**
 * #177: live-streamed suppress routes stream their narrative as a preview;
 * the final metadata's `authoritative_final_text` (the deterministic
 * Section-1 lead-in) replaces the accumulated answer at finalization. Pure
 * so the swap decision is testable without a stream harness.
 */
export function resolveAuthoritativeFinalText(meta: Record<string, unknown>): string | undefined {
  const value = meta.authoritative_final_text;
  return typeof value === 'string' && value.trim() ? value : undefined;
}

export interface CommandStreamDeps {
  /** thread.addMessage — used for the submit-failure assistant bubble. */
  addMessage: (threadId: string, message: MessageWithDate) => void;
  /** App's messagesRef-backed updater (keeps the transcript ref in sync). */
  updateAssistantMessage: (
    threadId: string,
    messageId: string,
    updater: (m: MessageWithDate) => MessageWithDate,
  ) => void;
  setCommandLoading: (loading: boolean) => void;
  setPendingCommand: (command: string | null) => void;
  /** Optional admin traffic-inspector feed. */
  pushMarchSseEvent?: (
    event: { channel?: string; event?: string; data?: Record<string, unknown> },
    runId: string,
  ) => void;
  /** Exports-drawer side effects for W_EXPORT_LIST / W_FILE_PDF (stays in App). */
  onExportArtifacts: (widget: StreamWidget) => void;
  isPlaceholderFinalText: (value: string) => boolean;
  /**
   * Refresh-resume (2026-08-11): the selected thread id. updateAssistantMessage
   * operates on the CURRENT thread's messages, so an interrupted stream is
   * resumed only once its thread is the selected one (on reload the last
   * active thread is restored as current, which is the refresh case).
   */
  currentThreadId?: string;
}

export interface StartCommandStreamArgs {
  message: string;
  threadId: string;
  tenantId: string;
  patientId: string;
  options: Record<string, unknown>;
}

interface AttachStreamArgs {
  runId: string;
  streamUrl: string;
  threadId: string;
  assistantId: string;
  patientId: string;
  /** The user message this stream answers (retry + resume marker). */
  commandText: string;
  /** #180 anchor: submit time for a fresh run, attach time for a resume. */
  clientSubmitStartedAtMs: number;
  /** Refresh-resume passes '0' so the backend replays the whole run. */
  lastEventId?: string;
  /**
   * True when this attach is a post-refresh resume. Enables the
   * persisted-turn safety net below: if a resumed stream ends without ever
   * delivering the answer, the completed turn is fetched from the server
   * rather than leaving the clinician looking at a progress preview.
   */
  isResume?: boolean;
}

export function useCommandStream(deps: CommandStreamDeps) {
  const {
    addMessage,
    updateAssistantMessage,
    setCommandLoading,
    setPendingCommand,
    pushMarchSseEvent,
    onExportArtifacts,
    isPlaceholderFinalText,
    currentThreadId,
  } = deps;

  const activeStreamCloseRef = useRef<(() => void) | null>(null);
  const activeAssistantRef = useRef<{
    threadId: string;
    assistantId: string;
    conversationId: string;
    patientId: string;
  } | null>(null);
  /** Resets on every SSE / widget activity — fires only if the stream goes silent. */
  const streamIdleWatchdogRef = useRef<number | null>(null);
  /** Wall-clock cap from stream start — avoids infinite streams even when events trickle. */
  const streamMaxDurationWatchdogRef = useRef<number | null>(null);
  /** Refresh-resume: one attempt per interrupted conversation per mount. */
  const resumeAttemptRef = useRef<string | null>(null);

  const clearActiveStream = useCallback(() => {
    if (streamIdleWatchdogRef.current !== null) {
      window.clearTimeout(streamIdleWatchdogRef.current);
      streamIdleWatchdogRef.current = null;
    }
    if (streamMaxDurationWatchdogRef.current !== null) {
      window.clearTimeout(streamMaxDurationWatchdogRef.current);
      streamMaxDurationWatchdogRef.current = null;
    }
    activeStreamCloseRef.current = null;
    activeAssistantRef.current = null;
  }, []);

  const stopStreaming = useCallback(
    async (reason: 'user' | 'idle' | 'max_duration' = 'user') => {
      const active = activeAssistantRef.current;
      if (!active) return;
      const close = activeStreamCloseRef.current;
      if (close) close();
      clearActiveStream();
      setCommandLoading(false);
      setPendingCommand(null);
      const suffix =
        reason === 'idle'
          ? 'The stream closed automatically because no data arrived for a while. That is not the same as clicking Stop—you can try the same question again.'
          : reason === 'max_duration'
            ? 'The stream stopped automatically because this request exceeded the client’s maximum run time. Try a narrower question or split it into smaller steps.'
            : 'Request stopped by user.';
      updateAssistantMessage(active.threadId, active.assistantId, (m) => {
        const existing = String(m.answer ?? '').trim();
        return {
          ...m,
          answer: existing ? `${existing}\n\n${suffix}` : suffix,
          isStreaming: false,
          streamPhase: undefined,
        };
      });
      clearActiveStreamRecord(active.conversationId);
      try {
        await cancelCommandRun(active.conversationId, active.patientId);
      } catch {
        // Keep UX responsive even if backend cancellation endpoint fails.
      }
    },
    [clearActiveStream, setCommandLoading, setPendingCommand, updateAssistantMessage],
  );

  // Close the live stream + watchdogs if the workspace unmounts mid-stream.
  // Deliberately does NOT clear the resume marker or cancel the run: the
  // backend keeps executing, and the marker is what lets the next mount
  // (reload or SPA navigation back) re-attach to it.
  useEffect(() => {
    return () => {
      const close = activeStreamCloseRef.current;
      if (close) close();
      if (streamIdleWatchdogRef.current !== null) {
        window.clearTimeout(streamIdleWatchdogRef.current);
      }
      if (streamMaxDurationWatchdogRef.current !== null) {
        window.clearTimeout(streamMaxDurationWatchdogRef.current);
      }
    };
  }, []);

  const attachStreamForAssistant = useCallback(
    ({
      runId,
      streamUrl,
      threadId,
      assistantId,
      patientId,
      commandText,
      clientSubmitStartedAtMs,
      lastEventId,
      isResume,
    }: AttachStreamArgs) => {
      // Refresh-resume marker: written while the stream is live, cleared on
      // done/error/stop. Legacy one-shot POST /chat streams cannot be
      // re-attached server-side, so they never record one.
      if (!streamUrl.startsWith('legacy-post:')) {
        recordActiveStream({
          conversationId: runId,
          threadId,
          assistantId,
          patientId,
          command: commandText,
          startedAt: Date.now(),
        });
      }

      let streamCtx = STREAM_INITIAL;
      // #119 (2026-08-01): the CMA latency deep dive could attribute the
      // server's own time but not the 1.8-7.5s between the server finishing
      // and the footer figure the clinician reads. client_processing_time_ms
      // measures the whole span as ONE number, so it cannot say whether the
      // time went on waiting for the server or on streaming + rendering
      // afterwards. Anchoring the first final token splits it into exactly
      // those two halves. Null until a final token arrives — an end-flush
      // burst legitimately has none, and reporting 0 there would read as an
      // instant first token rather than "not applicable".
      let clientFirstFinalTokenAtMs: number | null = null;
      // #44: set once the final metadata delivers the complete answer.
      // From that point the accumulated token preview is superseded, so
      // later tokens must not append to it. Per-stream, so a new run
      // starts clean. See the long note at the response.metadata handler.
      let finalAnswerIsAuthoritative = false;
      // Safety net for resumed streams: set once the answer itself arrives
      // (final metadata or a real answer token). If a resumed stream ends
      // without this, the run's answer never reached the bubble and the
      // persisted turn is fetched instead — see onDone.
      let resumeReceivedAnswer = false;
      const parseStreamMs = (key: string, fallback: number) => {
        const n = Number.parseInt(String(import.meta.env[key] ?? ''), 10);
        return Number.isFinite(n) && n >= 15_000 ? n : fallback;
      };
      // Idle timeout: no SSE/widget activity for this long → treat as stalled (legacy VITE_COMMAND_STREAM_TIMEOUT_MS used if idle-specific unset).
      const idleMs = Math.max(
        60_000,
        parseStreamMs(
          'VITE_COMMAND_STREAM_IDLE_TIMEOUT_MS',
          parseStreamMs('VITE_COMMAND_STREAM_TIMEOUT_MS', 300_000),
        ),
      );
      // Absolute cap from first byte of this stream session (default 30 minutes).
      const maxDurationMs = Math.max(
        idleMs + 60_000,
        parseStreamMs('VITE_COMMAND_STREAM_MAX_DURATION_MS', 1_800_000),
      );

      const bumpStreamActivity = () => {
        if (streamIdleWatchdogRef.current !== null) {
          window.clearTimeout(streamIdleWatchdogRef.current);
        }
        streamIdleWatchdogRef.current = window.setTimeout(() => {
          void stopStreaming('idle');
          toast.error('No data received for a while. The stream was closed. You can try again.');
        }, idleMs);
      };

      if (streamIdleWatchdogRef.current !== null) {
        window.clearTimeout(streamIdleWatchdogRef.current);
        streamIdleWatchdogRef.current = null;
      }
      if (streamMaxDurationWatchdogRef.current !== null) {
        window.clearTimeout(streamMaxDurationWatchdogRef.current);
        streamMaxDurationWatchdogRef.current = null;
      }
      streamMaxDurationWatchdogRef.current = window.setTimeout(() => {
        void stopStreaming('max_duration');
        toast.error('This request exceeded the maximum run time and was stopped.');
      }, maxDurationMs);
      bumpStreamActivity();

      const closeStream = openCommandStream(
        runId,
        streamUrl,
        {
          onSseEvent: (payload) => {
            bumpStreamActivity();
            pushMarchSseEvent?.(
              {
                channel: payload?.channel,
                event: payload?.event,
                data: payload?.data as Record<string, unknown>,
              },
              runId,
            );
            const eventName = payload?.event ?? '';
            const done = payload?.done === true;

            if (payload?.channel === 'thinking' && eventName) {
              const [next, markComplete] = streamStateReducer(streamCtx, {
                type: 'THINKING_EVENT',
                payload: { event: eventName, done: done || eventName === 'thinking.complete' },
              });
              streamCtx = next;
              const eventData = (payload.data ?? {}) as Record<string, unknown>;
              const normalized = normalizeThinkingEvent(eventName, eventData, assistantId);
              updateAssistantMessage(threadId, assistantId, (m) => {
                let prev = ((m as any)._normalizedEvents ?? []) as NormalizedThinkingEvent[];
                if (markComplete) prev = markAllThinkingStepsComplete(prev);
                // The final phase is one-way. Emitting any final token forces
                // the hub's thinking->final handoff (SSEStreamingHub.
                // emit_final_token), but the backend keeps pushing step events
                // on the thinking channel afterwards -- a CMA route emits its
                // Stage 2/3 stages long after a Stage 1 lead-in has landed.
                // Demoting the phase back to 'thinking' on those made the UI
                // flip between the answer body and the thinking placeholder
                // for the rest of the run. Later steps still append to the
                // panel; they just no longer rewind the phase.
                const finalPhaseStarted = m.streamPhase === 'final';
                return {
                  ...m,
                  _normalizedEvents: [...prev, normalized],
                  isStreaming: true,
                  streamPhase: finalPhaseStarted ? 'final' : 'thinking',
                  thinkingComplete: finalPhaseStarted || streamCtx.hasSeenThinkingDone,
                };
              });
              return;
            }
            if (payload?.channel === 'final' && eventName === 'response.metadata') {
              const meta = (payload.data ?? {}) as Record<string, unknown>;
              const intentType = typeof meta.intent_type === 'string' ? meta.intent_type : undefined;
              const chartBriefingTurn = intentType === 'chart_briefing';
              // #177: live-streamed narrative routes send the streamed body as a
              // preview only; when the backend marks an authoritative final text
              // (the deterministic Section-1 lead-in), it replaces the
              // accumulated answer so the renderer's duplicate-suppression no
              // longer erases Section 1.
              const authoritativeFinalText = resolveAuthoritativeFinalText(meta);
              // #44: once the authoritative text lands, the answer is
              // complete and no later token may add to it.
              //
              // The backend emits response.metadata BEFORE it tokenizes
              // `response_text` on every end-flush route
              // (concrete_chatbot.py: emit_final_metadata, then the
              // _tokenize loop). So on any route that does not blank
              // stream_text -- clarification and the other terminals, which
              // carry no structured content -- the client set `answer` to
              // the whole message here and then appended the whole message
              // again token by token below. Live: a clarification ending
              // "...in the message box.message box.".
              //
              // Suppressing the emission backend-side would have broken
              // #23, which deliberately requires every route to carry the
              // authoritative text so Section 1 can never be lost to a
              // dropped token. Both contracts hold if the client simply
              // treats the authoritative text as terminal: tokens are a
              // PREVIEW of the answer, so once the real thing has arrived
              // the preview has nothing left to contribute.
              //
              // Ordering is what makes this safe, and it is fixed rather
              // than racy. Routes that genuinely stream (chart briefing and
              // friends) push their tokens through event_sink DURING
              // execution, long before this metadata event -- those still
              // accumulate normally. Only tokens arriving AFTER the
              // authoritative text are ignored, and those are re-emissions
              // of text the client already holds in full.
              if (authoritativeFinalText) {
                finalAnswerIsAuthoritative = true;
              }
              resumeReceivedAnswer = true;
              // #180: this is the "final metadata applied" commit point the footer
              // timer anchors to — captured once per stream, independent of how
              // many (if any) stream.token events preceded it.
              const clientProcessingTimeMs = performance.now() - clientSubmitStartedAtMs;
              updateAssistantMessage(threadId, assistantId, (m) => ({
                ...m,
                ...(chartBriefingTurn
                  ? { widgets: undefined, widget: undefined }
                  : {}),
                ...(authoritativeFinalText ? { answer: authoritativeFinalText } : {}),
                responseMetadata: {
                  ...decodeResponseMetadata(meta, { chartBriefingTurn }),
                  client_processing_time_ms: clientProcessingTimeMs,
                  // #119: the two halves of the number above. time_to_first_token
                  // is submit -> first rendered token (network + server); the
                  // stream_and_render remainder is everything after it. Both
                  // null on an end-flush turn that never streamed a token, so a
                  // missing measurement is never mistaken for a fast one.
                  client_time_to_first_token_ms:
                    clientFirstFinalTokenAtMs === null
                      ? null
                      : clientFirstFinalTokenAtMs - clientSubmitStartedAtMs,
                  client_stream_and_render_ms:
                    clientFirstFinalTokenAtMs === null
                      ? null
                      : performance.now() - clientFirstFinalTokenAtMs,
                },
              }));
              return;
            }
            if (payload?.channel === 'final' && eventName === 'stream.token') {
              const token = String((payload?.data as Record<string, unknown> | undefined)?.token ?? '');
              if (!token || isPlaceholderFinalText(token.trim())) return;
              const [next, markComplete] = streamStateReducer(streamCtx, { type: 'FIRST_FINAL_TOKEN' });
              if (clientFirstFinalTokenAtMs === null) {
                clientFirstFinalTokenAtMs = performance.now();
              }
              streamCtx = next;
              updateAssistantMessage(threadId, assistantId, (m) => {
                let events = ((m as any)._normalizedEvents ?? []) as NormalizedThinkingEvent[];
                if (markComplete) events = markAllThinkingStepsComplete(events);
                return {
                  ...m,
                  _normalizedEvents: events,
                  thinkingComplete: true,
                  _firstFinalTokenAt: (m as any)._firstFinalTokenAt ?? new Date().toISOString(),
                  // #44: keep the authoritative answer verbatim. The token
                  // still drives stream state above (phase handoff, thinking
                  // completion, first-token timing) — it just no longer
                  // doubles the text the clinician reads.
                  answer: finalAnswerIsAuthoritative
                    ? String(m.answer ?? '')
                    : `${String(m.answer ?? '')}${token}`,
                  isStreaming: true,
                  streamPhase: 'final',
                };
              });
              return;
            }
            if (payload?.channel === 'final' && eventName === 'stream.end') {
              const [next] = streamStateReducer(streamCtx, { type: 'FINAL_DONE' });
              streamCtx = next;
              updateAssistantMessage(threadId, assistantId, (m) => ({
                ...m,
                thinkingComplete: true,
              }));
              return;
            }
            if (payload?.channel === 'final' && eventName === 'stream.error') {
              const msg = String((payload?.data as Record<string, unknown> | undefined)?.message ?? 'Stream error');
              const [next, markComplete] = streamStateReducer(streamCtx, { type: 'ERROR', payload: { message: msg } });
              streamCtx = next;
              updateAssistantMessage(threadId, assistantId, (m) => {
                let events = ((m as any)._normalizedEvents ?? []) as NormalizedThinkingEvent[];
                if (markComplete) events = markAllThinkingStepsComplete(events);
                return { ...m, _normalizedEvents: events, thinkingComplete: true };
              });
            }
          },
          onWidget: (widget) => {
            bumpStreamActivity();
            updateAssistantMessage(threadId, assistantId, (m) => {
              const metaIntent = (m as { responseMetadata?: { intent_type?: string } }).responseMetadata
                ?.intent_type;
              if (metaIntent === 'chart_briefing') {
                // Keep chart_briefing narrative visible even when backend/client fallback uses
                // W_SUMMARY_CLINICAL instead of final stream.token events.
                const fallbackNarrative = chartBriefingNarrativeFromWidget(
                  metaIntent,
                  { type: widget.type, data: widget.data as Record<string, unknown> },
                );
                if (fallbackNarrative) {
                  return {
                    ...m,
                    answer: fallbackNarrative,
                    widget: undefined,
                    widgets: undefined,
                    isStreaming: true,
                  };
                }
                if (widget.type !== 'W_EXPORT_LIST' && widget.type !== 'W_FILE_PDF') {
                  return m;
                }
              }
              const prevWidgets = (m as { widgets?: Array<StreamWidget> }).widgets ?? (m.widget ? [m.widget] : []);
              const withoutSame = prevWidgets.filter((w) => w.type !== widget.type);
              const nextWidgets = [...withoutSame, { type: widget.type, data: widget.data }];
              let answer = m.answer;
              if (widget.type === 'W_SUMMARY_CLINICAL' && !String(m.answer ?? '').trim()) {
                answer = String((widget.data as Record<string, unknown>)?.summary ?? '');
              } else if (widget.type === 'W_FHIR_PATIENT') {
                const wd = widget.data as Record<string, unknown>;
                if (wd?.found === true) {
                  // Do not inject a preamble — it concatenates with streamed tokens (e.g. "…demographics.Cardiology").
                  answer = String(m.answer ?? '');
                } else {
                  answer = String(wd?.message ?? 'Patient demographics were not found in the session data.');
                }
              }
              return {
                ...m,
                answer: answer ?? m.answer,
                widget: nextWidgets.length === 1 ? nextWidgets[0] : undefined,
                widgets: nextWidgets.length > 0 ? nextWidgets : undefined,
                isStreaming: true,
              };
            });
            // Feed exports into Tools drawer (App-owned side effect).
            onExportArtifacts({ type: widget.type, data: widget.data as Record<string, unknown> });
          },
          onError: (errorMessage: string) => {
            setCommandLoading(false);
            setPendingCommand(null);
            closeStream();
            clearActiveStream();
            clearActiveStreamRecord(runId);
            const [, markComplete] = streamStateReducer(streamCtx, { type: 'ERROR', payload: { message: errorMessage } });
            updateAssistantMessage(threadId, assistantId, (m) => {
              let events = ((m as any)._normalizedEvents ?? []) as NormalizedThinkingEvent[];
              if (markComplete) events = markAllThinkingStepsComplete(events);
              const existing = String(m.answer ?? '').trim();
              const fallback = errorMessage || 'Stream error. The connection may have been interrupted.';
              return {
                ...m,
                _normalizedEvents: events,
                thinkingComplete: true,
                answer: existing ? `${existing}\n\n${fallback}` : fallback,
                isStreaming: false,
                streamPhase: undefined,
                retryCommand: commandText,
              };
            });
          },
          onDone: () => {
            closeStream();
            setCommandLoading(false);
            setPendingCommand(null);
            clearActiveStream();
            clearActiveStreamRecord(runId);
            updateAssistantMessage(threadId, assistantId, (m) => {
              const widgets = (m as { widgets?: Array<StreamWidget> }).widgets ?? (m.widget ? [m.widget] : []);
              let answer = String(m.answer ?? '');
              if (!answer.trim() || isPlaceholderFinalText(answer)) {
                if (widgets.some((w) => w.type === 'W_SUMMARY_CLINICAL')) {
                  const w = widgets.find((x) => x.type === 'W_SUMMARY_CLINICAL');
                  answer = String((w?.data as Record<string, unknown>)?.summary ?? '');
                } else if (widgets.some((w) => w.type === 'W_FHIR_PATIENT')) {
                  const w = widgets.find((x) => x.type === 'W_FHIR_PATIENT');
                  const wd = w?.data as Record<string, unknown>;
                  if (wd?.found === true) {
                    answer = answer.trim() ? answer : '';
                  } else {
                    answer = String(wd?.message ?? 'Patient demographics were not found in the session data.');
                  }
                } else if (widgets.length > 0) {
                  answer = '';
                }
              }
              return {
                ...m,
                isStreaming: false,
                streamPhase: undefined,
                answer: answer || m.answer,
              };
            });
            // Safety net (dev regression req-4e5d0d9ea..., 2026-08-11): a
            // resumed stream that ended without ever delivering the answer
            // used to leave the clinician staring at the mid-run progress
            // preview while the completed review sat persisted server-side and
            // invisible (the local transcript cache is non-empty, so nothing
            // else ever refetches it). The delivery bug behind that is fixed
            // in the hub; this is the belt-and-braces guarantee that no future
            // delivery gap can silently cost someone their answer.
            if (isResume && !resumeReceivedAnswer) {
              void fetchConversationTurns(runId)
                .then((turns) => {
                  const lastUserIdx = turns.map((t) => t.role).lastIndexOf('user');
                  const answerTurn = turns
                    .slice(lastUserIdx + 1)
                    .find((t) => t.role === 'assistant');
                  const recovered = String(answerTurn?.content ?? '').trim();
                  if (!recovered) return;
                  updateAssistantMessage(threadId, assistantId, (m) => {
                    // Never overwrite a longer answer the stream did deliver.
                    const current = String(m.answer ?? '').trim();
                    if (current.length >= recovered.length) return m;
                    return {
                      ...m,
                      answer: recovered,
                      isStreaming: false,
                      streamPhase: undefined,
                      thinkingComplete: true,
                    };
                  });
                })
                .catch(() => {
                  /* best-effort recovery */
                });
            }
          },
        },
        lastEventId !== undefined ? { lastEventId } : undefined,
      );
      activeStreamCloseRef.current = closeStream;
      activeAssistantRef.current = {
        threadId,
        assistantId,
        conversationId: runId,
        patientId,
      };
      // #68: bind the UI interaction stream to this conversation so the
      // combined-debug export can carry the click events alongside the
      // request trace. Fire-and-forget; never affects the stream.
      try {
        void import('../services/uiEventTelemetry').then((m) =>
          m.setUiTelemetryConversation(runId)
        );
      } catch {
        /* telemetry only */
      }
    },
    [
      clearActiveStream,
      isPlaceholderFinalText,
      onExportArtifacts,
      pushMarchSseEvent,
      setCommandLoading,
      setPendingCommand,
      stopStreaming,
      updateAssistantMessage,
    ],
  );

  const startCommandStream = useCallback(
    ({ message, threadId, tenantId, patientId, options }: StartCommandStreamArgs) => {
      // #180: footer "elapsed" must be client-measured end-to-end (submit -> final
      // metadata commit), not the server's self-reported processing_time_ms alone
      // (measured 0.7-3.5s below server duration live — a definition problem, not
      // a fixed offset). performance.now() anchors here, at the moment the POST is
      // fired; regime-independent by construction, since it spans submit to
      // whichever event actually carries the final metadata, whether that event
      // is the only byte the stream ever sends (end-flush burst) or arrives after
      // a run of token-streamed events.
      const clientSubmitStartedAtMs = performance.now();
      setCommandLoading(true);
      setPendingCommand(message.trim());
      submitCommand(message.trim(), tenantId, patientId, options, threadId)
        .then(({ runId, streamUrl }) => {
          const assistantId = `msg-${Date.now()}-assistant`;
          addMessage(threadId, {
            id: assistantId,
            role: 'assistant',
            answer: '',
            _normalizedEvents: [] as NormalizedThinkingEvent[],
            isStreaming: true,
            streamPhase: 'thinking',
            evidence: { runId, generated: new Date().toLocaleString() },
            timestamp: new Date(),
          } as unknown as MessageWithDate);

          attachStreamForAssistant({
            runId,
            streamUrl,
            threadId,
            assistantId,
            patientId,
            commandText: message.trim(),
            clientSubmitStartedAtMs,
          });
        })
        .catch((err: unknown) => {
          setCommandLoading(false);
          setPendingCommand(null);
          clearActiveStream();
          const errorMessage = err instanceof Error ? err.message : 'Command failed.';
          const response: MessageWithDate = {
            id: `msg-${Date.now()}`,
            role: 'assistant',
            answer: errorMessage,
            evidence: undefined,
            widget: undefined,
            timestamp: new Date(),
            retryCommand: message.trim(),
          };
          addMessage(threadId, response);
        });
    },
    [
      addMessage,
      attachStreamForAssistant,
      clearActiveStream,
      setCommandLoading,
      setPendingCommand,
    ],
  );

  // Refresh-resume (2026-08-11): after a reload (or SPA navigation back), if a
  // marker says a stream was interrupted on the selected thread, ask the
  // backend whether that run is still alive. Alive (or still queued) →
  // re-attach with a full replay (Last-Event-ID: 0) so the thinking panel and
  // answer rebuild from event 1. Finished → recover the persisted assistant
  // turn from /v1/conversations/{id}/turns. Neither → the load-time
  // "interrupted" annotation stands.
  useEffect(() => {
    if (!currentThreadId) return;
    const record = readActiveStream();
    if (!record || record.threadId !== currentThreadId) return;
    if (activeAssistantRef.current) return; // a live stream is already attached
    if (resumeAttemptRef.current === record.conversationId) return;
    resumeAttemptRef.current = record.conversationId;

    void (async () => {
      // An unanswerable probe (auth/runtime config still booting, transient
      // network) must never be read as "no run in flight" — that verdict
      // clears the marker and permanently discards a resumable answer. Retry
      // briefly; only a definitive reply decides the branch below.
      let status = await fetchChatStreamStatus(record.conversationId, record.patientId);
      for (let attempt = 0; attempt < 4 && !status.ok; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 400 * (attempt + 1)));
        status = await fetchChatStreamStatus(record.conversationId, record.patientId);
      }
      if (!status.ok) {
        // Still no verdict: leave the marker intact so a later mount (or the
        // user reopening the thread) can still resume, and allow a retry.
        resumeAttemptRef.current = null;
        return;
      }
      if (status.active || status.pending) {
        // Reset the bubble to a clean streaming state: the full replay
        // reconstructs thinking events and answer text from the start, so
        // nothing already-rendered (or the load-time "interrupted"
        // annotation) may be left to double up.
        updateAssistantMessage(record.threadId, record.assistantId, (m) => ({
          ...m,
          answer: '',
          _normalizedEvents: [] as NormalizedThinkingEvent[],
          widget: undefined,
          widgets: undefined,
          isStreaming: true,
          streamPhase: 'thinking',
          thinkingComplete: false,
        } as unknown as MessageWithDate));
        setCommandLoading(true);
        setPendingCommand(record.command || null);
        attachStreamForAssistant({
          runId: record.conversationId,
          streamUrl: buildChatStreamUrl(record.conversationId, record.patientId),
          threadId: record.threadId,
          assistantId: record.assistantId,
          patientId: record.patientId,
          commandText: record.command,
          clientSubmitStartedAtMs: performance.now(),
          lastEventId: '0',
          isResume: true,
        });
        return;
      }

      clearActiveStreamRecord(record.conversationId);
      // Run finished (or died) while the page was away — the answer, if any,
      // was persisted server-side. Recover the turn that answers the marker's
      // user message; if none exists, the "interrupted" annotation stands.
      try {
        const turns = await fetchConversationTurns(record.conversationId);
        const lastUserIdx = turns.map((t) => t.role).lastIndexOf('user');
        const answerTurn = turns.slice(lastUserIdx + 1).find((t) => t.role === 'assistant');
        if (answerTurn?.content?.trim()) {
          updateAssistantMessage(record.threadId, record.assistantId, (m) => ({
            ...m,
            answer: answerTurn.content,
            isStreaming: false,
            streamPhase: undefined,
            thinkingComplete: true,
          } as unknown as MessageWithDate));
        }
      } catch {
        // best-effort recovery; the interrupted annotation remains
      }
    })();
  }, [
    attachStreamForAssistant,
    currentThreadId,
    setCommandLoading,
    setPendingCommand,
    updateAssistantMessage,
  ]);

  return { startCommandStream, stopStreaming, clearActiveStream };
}
